import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api from '../services/api';
import { BoltIcon, CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Read exact selected plan, price, and billing cycle from landing page state or query parameters
  const selectedPlan = location.state?.selectedPlan || searchParams.get('plan') || 'Pro';
  const planPrice = location.state?.planPrice || searchParams.get('price') || '999';
  const billingCycle = location.state?.billingCycle || searchParams.get('billing_cycle') || 'month';

  const [gymName, setGymName] = useState('');
  const [gymAddress, setGymAddress] = useState('');
  const [gymPhone, setGymPhone] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gymName || !gymAddress || !gymPhone || !name || !email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    // Phone number validation
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(gymPhone)) {
      setError('Please enter a valid phone number (minimum 10 digits).');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // 1. Register Gym & Owner Account with selected plan
      const response = await api.post('/api/auth/register', {
        gym_name: gymName,
        gym_address: gymAddress,
        gym_phone: gymPhone,
        name,
        email,
        password,
        plan_name: selectedPlan
      });
      const { token, user } = response.data;

      // 2. Dynamically load Razorpay SDK
      const loadRazorpayScript = () => {
        return new Promise((resolve) => {
          if (window.Razorpay) {
            resolve(true);
            return;
          }
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => resolve(true);
          script.onerror = () => resolve(false);
          document.body.appendChild(script);
        });
      };

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Razorpay payment SDK failed to load. Please check your internet connection and try again.');
        setLoading(false);
        return;
      }

      // 3. Open Razorpay Checkout Modal for chosen plan
      const options = {
        key: 'rzp_live_TPB6wlQ8fTh6mu', // Connected Live Razorpay Key ID
        amount: parseFloat(planPrice) * 100, // Amount in paise
        currency: 'INR',
        name: 'SmartGoNext Gym Platform',
        description: `SaaS Subscription - ${selectedPlan} Plan (₹${planPrice}/${billingCycle})`,
        handler: async function (paymentResponse) {
          // PAYMENT SUCCESSFUL! Log in user, activate subscription, and redirect to dashboard
          try {
            await api.post(
              '/api/saas/subscribe-plan',
              {
                plan_name: selectedPlan,
                amount: parseFloat(planPrice),
                payment_method: 'Razorpay Gateway',
                razorpay_payment_id: paymentResponse.razorpay_payment_id
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
          } catch (payErr) {
            console.error('Error logging Razorpay payment backend:', payErr);
          } finally {
            // Log in and go to dashboard only after successful payment!
            login(token, user);
            navigate('/dashboard');
          }
        },
        prefill: {
          name: name,
          email: email,
          contact: gymPhone
        },
        theme: {
          color: '#f97316'
        },
        modal: {
          ondismiss: function() {
            // User closed Razorpay popup without paying - DO NOT LOG IN or go to dashboard!
            setError('Payment was not completed. Please complete payment to activate your gym account and access the dashboard.');
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (errResp) {
        setError('Payment failed: ' + (errResp.error?.description || 'Transaction declined. Please try again.'));
        setLoading(false);
      });
      rzp.open();

    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Registration failed. Please verify network and try again.';
      const errorDetails = err.response?.data?.details;
      setError(errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage);
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Register Gym - SmartGoNext Gym Management Software Worldwide"
        description="Register your gym with SmartGoNext Gym — the best gym management software worldwide. Selected plan subscription payment powered by Razorpay."
        canonicalUrl="https://gym.smartgonext.com/register"
        type="website"
        noIndex={false}
      />
      <div className="bg-white min-h-screen flex flex-col relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-orange-100/30 rounded-full blur-[100px] pointer-events-none"></div>

      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
        <div className="w-full max-w-lg">
          {/* Brand */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-3xl font-extrabold text-gray-900">
              <BoltIcon className="w-10 h-10 text-orange-500" />
              <span className="text-orange-500">
                SmartGoNext Gym
              </span>
            </Link>
            <p className="text-gray-600 mt-2 text-sm">Complete your registration & payment to access workspace</p>
          </div>

          {/* Card */}
          <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-xl space-y-6">
            <h2 className="text-xl font-bold text-gray-900 text-center">Gym Registration</h2>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-xs p-4 rounded-xl">
                <span className="font-bold">Error:</span> {error}
              </div>
            )}

            {/* Selected Plan Summary Banner (Passed from Landing Page) */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 p-4 rounded-2xl flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 text-white rounded-xl flex items-center justify-center shadow-md">
                  <SparklesIcon className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black uppercase text-orange-600 tracking-wider">
                      Selected Plan
                    </span>
                    <span className="bg-orange-200 text-orange-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {selectedPlan}
                    </span>
                  </div>
                  <div className="text-lg font-black text-gray-900 mt-0.5">
                    ₹{planPrice}{' '}
                    <span className="text-xs font-semibold text-gray-500">
                      / {billingCycle}
                    </span>
                  </div>
                </div>
              </div>

              <Link
                to="/"
                className="text-xs font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2"
              >
                Change Plan
              </Link>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Gym Name
                </label>
                <input
                  type="text"
                  required
                  disabled={loading}
                  value={gymName}
                  onChange={(e) => setGymName(e.target.value)}
                  placeholder="Elite Fitness Academy"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-400 focus:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Gym Address
                </label>
                <textarea
                  required
                  disabled={loading}
                  value={gymAddress}
                  onChange={(e) => setGymAddress(e.target.value)}
                  placeholder="123 Fitness Street, Health District, City, State, ZIP"
                  rows="2"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-400 focus:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Gym Phone Number
                </label>
                <input
                  type="tel"
                  required
                  disabled={loading}
                  value={gymPhone}
                  onChange={(e) => setGymPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-400 focus:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Owner's Name
                </label>
                <input
                  type="text"
                  required
                  disabled={loading}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Mercer"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-400 focus:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  disabled={loading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="owner@yourgym.com"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-400 focus:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  disabled={loading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-400 focus:bg-white rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 px-4 rounded-xl transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm mt-3 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Registering & Opening Razorpay...
                  </span>
                ) : (
                  `Register Gym & Pay ₹${planPrice} via Razorpay`
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-xs text-gray-500">
              Already have a tenant workspace?{' '}
              <Link to="/login" className="text-orange-500 hover:underline font-semibold">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}
